import { useCallback, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';

import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { log } from '../../loggers/Logger';
import { SaveWorkingCopyError } from '../../services/Errors';
import { removeSectionInPage } from '../../services/PageOperations';

/** Presentation state for the "Delete section" confirmation modal on the Contents tab. */
export interface DeleteSectionButtonViewModel {
  readonly isModalVisible: boolean;
  /** True while the section deletion is being persisted. */
  readonly isDeleting: boolean;
  readonly openModal: () => void;
  readonly dismissModal: () => void;
  /** Persist the section deletion and close the modal. No-op when already deleting. */
  readonly confirmDelete: () => void;
}

/**
 * Drives the "Delete section" confirmation modal for a Guide page section.
 * Removes the section from its parent page's contentSections via
 * PageOperations.removeSectionInPage, then stages and flushes through
 * ContentEditorService.
 */
export function useDeleteSectionButtonViewModel(
  currentPage: ContentNode,
  currentSection: ContentNode,
): DeleteSectionButtonViewModel {
  const service = useContentEditorService();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // When the user clicks the trash icon, show the confirmation.
  const openModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  // When the modal is dismissed, reset state.
  const dismissModal = useCallback(() => {
    setIsModalVisible(false);
    setIsDeleting(false);
  }, []);

  // When the user confirms, remove the section and close the modal.
  const confirmDelete = useCallback(() => {
    if (isDeleting) return;
    const content = service.content;
    if (!content) return;
    const updated = removeSectionInPage(content, currentPage.id, currentSection.id);
    if (!updated) return;
    setIsDeleting(true);
    void (async () => {
      try {
        service.stageEdit(updated);
        await service.flush();
        // Successful flush triggers a parent re-render that unmounts this
        // component (the deleted section is no longer rendered). Skip
        // dismissModal/setIsDeleting — they would warn about state updates
        // on an unmounted component.
      } catch (error) {
        setIsDeleting(false);
        // Only handle the save-failure case inline; rethrowing anything else
        // turns it into an unhandled rejection so RUM's global
        // `unhandledrejection` listener reports it.
        if (!(error instanceof SaveWorkingCopyError)) throw error;
        log.error({ err: error }, 'Failed to save section deletion to working copy');
        // The flashbar persists across the re-render that would otherwise
        // unmount any inline alert in this modal, and gives the user a
        // retry path.
        FlashbarMessageBroker.create().pushErrorMessage(
          'There was an error deleting the section. Please reload the page and try again.',
          undefined,
          undefined,
          'Reload',
          () => window.location.reload(),
        );
      }
    })();
  }, [isDeleting, service, currentPage, currentSection]);

  return {
    isModalVisible,
    isDeleting,
    openModal,
    dismissModal,
    confirmDelete,
  };
}
